import { computed, ref } from 'vue';
import { http } from '@/interceptors/axiosRequestInterceptor';
import { can } from '@/composables';
import { getImpersonation } from '@/composables/impersonation';

/**
 * AB#22138 — phone notifications on this device (Web Push).
 *
 * The account menu's "Phone notifications" switch. Turning it on asks the
 * browser for permission (that request has to come from a tap), registers the
 * service worker (public/sw.js, which only shows notifications and caches
 * nothing) and sends the device's subscription to the server. Turning it off,
 * or signing out, removes this device.
 *
 * Offered only to holders of receive-push-notifications, never in an
 * impersonated session, and only when the server has its keys set up. On an
 * iPhone the browser offers it only once the portal is on the Home Screen
 * (iOS 16.4+); `needsInstall` says so.
 */
export const PUSH_PERMISSION = 'receive-push-notifications';
const SW_URL = '/sw.js';

type State = 'unsupported' | 'needs-install' | 'blocked' | 'off' | 'on';

/** The kinds of notification, as the server names them (PushPreferences). */
export type PushType = 'chat' | 'desk' | 'assigned' | 'account_orders' | 'delayed';
export interface PushPreference {
  type: PushType;
  enabled: boolean;
}

const state = ref<State>('off');
const busy = ref(false);
const serverEnabled = ref(false);
const publicKey = ref<string | null>(null);
let loaded: Promise<void> | null = null;
/** The kinds that can reach this person, each on unless they switched it off. */
const preferences = ref<PushPreference[]>([]);
/** The Notifications sheet (NotificationSettingsDrawer, mounted in App.vue). */
const settingsOpen = ref(false);

const isIos = (): boolean =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

const isStandalone = (): boolean =>
  window.matchMedia?.('(display-mode: standalone)').matches === true ||
  (navigator as unknown as { standalone?: boolean }).standalone === true;

export const pushSupported = (): boolean =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

const b64ToBytes = (b64url: string): Uint8Array => {
  const pad = '='.repeat((4 - (b64url.length % 4)) % 4);
  const raw = atob((b64url + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
};

const currentSubscription = async (): Promise<PushSubscription | null> => {
  const reg = await navigator.serviceWorker.getRegistration(SW_URL);
  return reg ? reg.pushManager.getSubscription() : null;
};

const work = async (): Promise<void> => {
  if (!pushSupported()) {
    state.value = isIos() && !isStandalone() ? 'needs-install' : 'unsupported';
    return;
  }
  try {
    const { data } = await http.get('push/config');
    serverEnabled.value = Boolean(data?.result?.enabled) && !data?.result?.impersonated;
    publicKey.value = data?.result?.public_key ?? null;
    preferences.value = Array.isArray(data?.result?.preferences) ? data.result.preferences : [];
  } catch {
    serverEnabled.value = false;
  }
  if (Notification.permission === 'denied') {
    state.value = 'blocked';
    return;
  }
  state.value = (await currentSubscription()) ? 'on' : 'off';
};

export const usePushNotifications = () => {
  const allowed = computed(() => can(PUSH_PERMISSION) && !getImpersonation());
  const offered = computed(
    () =>
      allowed.value &&
      (state.value === 'needs-install' || (serverEnabled.value && state.value !== 'unsupported'))
  );

  const load = (): Promise<void> => {
    if (!allowed.value) return Promise.resolve();
    loaded ??= work();
    return loaded;
  };

  /** Must be called from a tap: the browser only asks for permission then. */
  const enable = async (): Promise<boolean> => {
    if (!pushSupported() || !publicKey.value || busy.value) return false;
    busy.value = true;
    try {
      const answer = await Notification.requestPermission();
      if (answer !== 'granted') {
        state.value = answer === 'denied' ? 'blocked' : 'off';
        return false;
      }
      const reg = await navigator.serviceWorker.register(SW_URL, { scope: '/' });
      await navigator.serviceWorker.ready;
      const sub =
        (await reg.pushManager.getSubscription()) ??
        (await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: b64ToBytes(publicKey.value) as unknown as BufferSource,
        }));
      await http.post('push/subscriptions', sub.toJSON());
      state.value = 'on';
      return true;
    } catch {
      return false;
    } finally {
      busy.value = false;
    }
  };

  const disable = async (): Promise<void> => {
    if (busy.value) return;
    busy.value = true;
    try {
      const sub = pushSupported() ? await currentSubscription() : null;
      if (sub) {
        await http.post('push/subscriptions/delete', { endpoint: sub.endpoint }).catch(() => {});
        await sub.unsubscribe().catch(() => false);
      }
      state.value = 'off';
    } finally {
      busy.value = false;
    }
  };

  /** A test notification to every device of mine that has it on. */
  const test = async (): Promise<boolean> => {
    try {
      await http.post('push/test');
      return true;
    } catch {
      return false;
    }
  };

  /**
   * AB#22138 — switch one kind on or off, for every device of this person.
   * Shown at once; put back if the server says no.
   */
  const setPreference = async (type: PushType, enabled: boolean): Promise<boolean> => {
    const before = preferences.value.map((p) => ({ ...p }));
    preferences.value = preferences.value.map((p) => (p.type === type ? { ...p, enabled } : p));
    try {
      const { data } = await http.post('push/preferences', { type, enabled });
      if (Array.isArray(data?.result?.preferences)) preferences.value = data.result.preferences;
      return true;
    } catch {
      preferences.value = before;
      return false;
    }
  };

  const openSettings = (): void => {
    settingsOpen.value = true;
  };

  return {
    state,
    busy,
    offered,
    load,
    enable,
    disable,
    test,
    preferences,
    setPreference,
    settingsOpen,
    openSettings,
  };
};

/**
 * Signing out: this device stops getting the person's notifications. Bounded,
 * so a slow network never holds the sign-out up.
 */
export const endPushOnThisDevice = async (timeoutMs = 2500): Promise<void> => {
  if (!pushSupported()) return;
  const job = (async () => {
    const sub = await currentSubscription();
    if (!sub) return;
    await http.post('push/subscriptions/delete', { endpoint: sub.endpoint }).catch(() => {});
    await sub.unsubscribe().catch(() => false);
    if ('clearAppBadge' in navigator) {
      await (navigator as unknown as { clearAppBadge: () => Promise<void> })
        .clearAppBadge()
        .catch(() => {});
    }
  })().catch(() => {});
  await Promise.race([job, new Promise((r) => setTimeout(r, timeoutMs))]);
};

/**
 * The number on the installed app's icon follows Order chats' unread count
 * while the portal is open (the service worker sets it when a push arrives).
 */
export const syncAppBadge = (count: number): void => {
  const nav = navigator as unknown as {
    setAppBadge?: (n: number) => Promise<void>;
    clearAppBadge?: () => Promise<void>;
  };
  try {
    if (count > 0) void nav.setAppBadge?.(count)?.catch(() => {});
    else void nav.clearAppBadge?.()?.catch(() => {});
  } catch {
    // No badging on this browser.
  }
};

/** For tests. */
export const __resetPushForTests = (): void => {
  loaded = null;
  state.value = 'off';
  serverEnabled.value = false;
  publicKey.value = null;
  preferences.value = [];
  settingsOpen.value = false;
};
